import type { MrpIncomingRow } from "@/lib/mrp";

import {
  incomingRowsEligibleForKitting,
  kittingRowExcludedAsStationPrestock,
  type KittingCartSlot,
} from "./kittingCartModel";
import {
  destinationSlotIdForIncomingRow,
  formatKittingDestinationLabel,
  kittingPickJobRefForIncomingRow,
  kittingPickQuantitiesFromIncomingRow,
  type KittingPickJobRef,
  type KittingPickLine,
} from "./kittingPickModel";

/** Stable demand key for one incoming row on one cart destination (excludes storage stop). */
export function kittingPickDemandKey(line: Pick<KittingPickLine, "id">): string {
  const parts = line.id.split("::");
  if (parts.length >= 2) {
    return `${parts[0]}::${parts[1]}`;
  }
  return line.id;
}

export function kittingPickDemandKeyForIncomingRow(
  row: MrpIncomingRow,
  destinationSlotId: string,
): string {
  return `${String(row.id)}::${destinationSlotId}`;
}

export interface KittingPickCartContainer {
  hex: string;
  shortCode: string;
  quantity: number;
}

export interface KittingPickCartEntry {
  id: string;
  demandKey: string;
  sku: string;
  quantity: number;
  destinationSlotId: string;
  destinationLabel: string;
  jobLabel: string;
  job: KittingPickJobRef;
  containerShortCodes: string[];
  pickedContainers: KittingPickCartContainer[];
  unpickedContainerShortCodes?: string[];
}

export function kittingIncomingAllocatedQuantity(row: MrpIncomingRow): number {
  return kittingPickQuantitiesFromIncomingRow(row).qtyAllocated;
}

/** Incoming rows with hub-allocated quantity for cart slots (includes fully picked BOM lines). */
export function incomingRowsWithKittingAllocations(rows: MrpIncomingRow[]): MrpIncomingRow[] {
  return rows.filter((row) => {
    if (kittingRowExcludedAsStationPrestock(row)) return false;
    return kittingIncomingAllocatedQuantity(row) > 0;
  });
}

export function buildKittingPickCartEntries(params: {
  incomingRows: MrpIncomingRow[];
  cartSlotById: Map<string, KittingCartSlot>;
  slotIds: readonly string[];
}): KittingPickCartEntry[] {
  const { incomingRows, cartSlotById, slotIds } = params;
  const slotFilter = new Set(slotIds);
  const eligible = incomingRowsWithKittingAllocations(incomingRows);
  const entries: KittingPickCartEntry[] = [];

  for (const row of eligible) {
    const destinationSlotId = destinationSlotIdForIncomingRow(row, { includeFullyAllocated: true });
    if (destinationSlotId == null || !slotFilter.has(destinationSlotId)) continue;
    const slot = cartSlotById.get(destinationSlotId);
    if (slot == null) continue;

    const sku = String(row.cpn ?? "").trim();
    if (sku.length === 0) continue;

    const quantity = kittingIncomingAllocatedQuantity(row);
    if (quantity <= 0) continue;

    const job = kittingPickJobRefForIncomingRow(row);
    const demandKey = kittingPickDemandKeyForIncomingRow(row, destinationSlotId);

    entries.push({
      id: `${demandKey}::${sku}`,
      demandKey,
      sku,
      quantity,
      destinationSlotId,
      destinationLabel: formatKittingDestinationLabel(slot),
      jobLabel: job.label,
      job,
      containerShortCodes: [],
      pickedContainers: [],
      unpickedContainerShortCodes: [],
    });
  }

  return sortKittingPickCartEntries(entries);
}

/** Cart truth from durable run items (physical quantities in A-G bins). */
export function buildKittingPickCartEntriesFromRunItems(params: {
  runItems: readonly {
    slotKey: string;
    componentSku: string;
    pickedQuantity: number;
    buildOrderId: number | null;
    transferOrderId: number | null;
    inventoryContainers?: readonly { hex?: string; shortCode: string; quantity?: number; picked?: boolean }[];
  }[];
  cartSlotById: Map<string, KittingCartSlot>;
  pickLines: readonly KittingPickLine[];
  slotIds: readonly string[];
}): KittingPickCartEntry[] {
  const slotFilter = new Set(params.slotIds);
  const entries: KittingPickCartEntry[] = [];

  for (const item of params.runItems) {
    if (!slotFilter.has(item.slotKey)) continue;
    const quantity = Math.max(0, Math.trunc(item.pickedQuantity));
    if (quantity <= 0) continue;
    const slot = params.cartSlotById.get(item.slotKey);
    if (slot == null) continue;

    const sku = item.componentSku.trim();
    if (sku.length === 0) continue;

    const matchingLines = params.pickLines.filter(
      (line) => line.destinationSlotId === item.slotKey && line.sku === sku,
    );
    const representative = matchingLines[0];
    const job =
      representative?.job ??
      ({
        kind: item.transferOrderId != null ? "transfer_order" : "work_order",
        label: slot.headlineLabel,
        workOrderId: item.buildOrderId ?? undefined,
        transferOrderId: item.transferOrderId ?? undefined,
      } as KittingPickJobRef);
    const demandKey =
      representative != null
        ? kittingPickDemandKey(representative)
        : `${item.slotKey}::${sku}`;
    const pickedContainers = (item.inventoryContainers ?? [])
      .filter((container) => container.picked !== false)
      .map((container) => ({
        hex: (container.hex ?? "").trim().toLowerCase(),
        shortCode: container.shortCode.trim(),
        quantity: Math.max(0, Math.trunc(container.quantity ?? 0)),
      }))
      .filter((container) => container.shortCode !== "" || container.hex !== "");

    entries.push({
      id: `${demandKey}::${sku}`,
      demandKey,
      sku,
      quantity,
      destinationSlotId: item.slotKey,
      destinationLabel: formatKittingDestinationLabel(slot),
      jobLabel: job.label,
      job,
      containerShortCodes: pickedContainers
        .map((container) => container.shortCode)
        .filter((shortCode) => shortCode !== ""),
      pickedContainers,
      unpickedContainerShortCodes: (item.inventoryContainers ?? [])
        .filter((container) => container.picked === false)
        .map((container) => container.shortCode.trim())
        .filter((shortCode) => shortCode !== ""),
    });
  }

  return sortKittingPickCartEntries(entries);
}

function sortKittingPickCartEntries(entries: KittingPickCartEntry[]): KittingPickCartEntry[] {
  return entries.sort((left, right) => {
    const slotCompare = left.destinationSlotId.localeCompare(right.destinationSlotId);
    if (slotCompare !== 0) return slotCompare;
    return left.sku.localeCompare(right.sku);
  });
}

export function kittingPickLineFromCartEntry(entry: KittingPickCartEntry): KittingPickLine {
  return {
    id: `${entry.demandKey}::cart`,
    storageLocation: "—",
    sku: entry.sku,
    description: entry.sku,
    qtyNeeded: entry.quantity,
    qtyAllocated: entry.quantity,
    qtyDue: 0,
    qtyOnHandHere: 0,
    destinationSlotId: entry.destinationSlotId,
    destinationLabel: entry.destinationLabel,
    job: entry.job,
    shortageNote: null,
  };
}

/** Representative pick line for a cart entry (first matching demand key in scoped pick lines). */
export function kittingPickLineForCartEntry(
  entry: KittingPickCartEntry,
  pickLines: readonly KittingPickLine[],
): KittingPickLine {
  const match = pickLines.find((line) => kittingPickDemandKey(line) === entry.demandKey && line.sku === entry.sku);
  if (match != null) return match;

  const fallback = pickLines.find((line) => kittingPickDemandKey(line) === entry.demandKey);
  return fallback ?? kittingPickLineFromCartEntry(entry);
}

/** Quantity picked without a container label. Container rows cover the rest. */
export function kittingPickUnlabeledQuantity(entry: {
  quantity: number;
  pickedContainers?: readonly { quantity: number }[];
}): number {
  const labeled = (entry.pickedContainers ?? []).reduce(
    (sum, container) => sum + Math.max(0, Math.trunc(container.quantity)),
    0,
  );
  return Math.max(0, Math.trunc(entry.quantity) - labeled);
}

export function kittingPickCartTotalQuantity(entries: readonly KittingPickCartEntry[]): number {
  return entries.reduce((sum, entry) => sum + entry.quantity, 0);
}

/** Rows that can still be picked (open demand) for tiles and scan flow. */
export function incomingRowsForKittingPickLines(rows: MrpIncomingRow[]): MrpIncomingRow[] {
  return incomingRowsEligibleForKitting(rows);
}
